import { useMemo, useState } from 'react';
import { ClipboardCheck, Layers, Plus, Minus, Check } from 'lucide-react';
import type { DeliverableRow, DeliverableSheet } from '../../types/deliverables';

interface Props {
  rows:        DeliverableRow[];
  editable:    boolean;
  onNotesChange?:   (row: DeliverableRow, notes: string) => void;
  onPercentChange?: (row: DeliverableRow, percent: number) => void;
  onAddLevel?:      (row: DeliverableRow) => void;
  onRemoveRow?:     (row: DeliverableRow) => void;
}

/** Una fila se considera "completada" cuando su % manual llega a 100. */
function isCompleted(r: DeliverableRow): boolean {
  return (r.percent_status ?? 0) >= 100;
}

/**
 * Tabla del entregable agrupada por (sheet → section → subsection).
 * - Tabs Modelado / Láminas.
 * - Cada fila muestra checkboxes E1/E2/E3 (solo si delivery_eN === true),
 *   anotación libre, y +/− cuando es leveled.
 *
 * En modo read-only los checkboxes y textareas siguen visibles pero
 * disabled — para que el docente vea exactamente lo que ve el alumno.
 */
export function DeliverableTable({ rows, editable, onNotesChange, onPercentChange, onAddLevel, onRemoveRow }: Props) {
  const [activeSheet, setActiveSheet] = useState<DeliverableSheet>('modelado');

  /** Agrupado: sheet → section → subsection → rows[] */
  const grouped = useMemo(() => {
    const out: Record<DeliverableSheet, Map<string, Map<string, DeliverableRow[]>>> = {
      modelado: new Map(),
      laminas:  new Map(),
    };
    const filtered = rows.filter((r) => r.sheet === activeSheet);
    for (const r of filtered) {
      const sec = r.section ?? '—';
      const sub = r.subsection ?? '—';
      if (!out[activeSheet].has(sec)) out[activeSheet].set(sec, new Map());
      const secMap = out[activeSheet].get(sec)!;
      if (!secMap.has(sub)) secMap.set(sub, []);
      secMap.get(sub)!.push(r);
    }
    return out[activeSheet];
  }, [rows, activeSheet]);

  /** Para saber el max level_index actual de un grupo (para "+") */
  const groupMaxLevel = (row: DeliverableRow): number => {
    if (!row.is_leveled) return 0;
    const baseActivity = row.activity.replace(/\sN\s*\d+\s*$/i, '');
    let max = 0;
    for (const r of rows) {
      if (!r.is_leveled) continue;
      const candidateBase = r.activity.replace(/\sN\s*\d+\s*$/i, '');
      if (candidateBase === baseActivity && (r.level_index ?? 0) > max) {
        max = r.level_index ?? 0;
      }
    }
    return max;
  };

  /** Última fila del grupo de niveles (para mostrar el + solo ahí) */
  const isLastOfLevelGroup = (row: DeliverableRow): boolean => {
    if (!row.is_leveled) return false;
    return (row.level_index ?? 0) === groupMaxLevel(row);
  };

  const sheetCount = (s: DeliverableSheet) => rows.filter((r) => r.sheet === s).length;

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-white/[0.06]">
        <SheetTab
          icon={ClipboardCheck}
          label="Modelado"
          count={sheetCount('modelado')}
          active={activeSheet === 'modelado'}
          onClick={() => setActiveSheet('modelado')}
        />
        <SheetTab
          icon={Layers}
          label="Láminas"
          count={sheetCount('laminas')}
          active={activeSheet === 'laminas'}
          onClick={() => setActiveSheet('laminas')}
        />
      </div>

      {/* Grupos */}
      <div className="space-y-6">
        {Array.from(grouped.entries()).map(([section, subMap]) => {
          // % de avance de esta sección/entrega — promedio del campo
          // "Estado" (percent_status) sobre todas las filas del grupo.
          // En Láminas las secciones son ENTREGA 1/2/3/DETALLES, así que
          // este número le dice al alumno cuánto lleva de cada entrega.
          const sectionRows: DeliverableRow[] = [];
          subMap.forEach((rs) => rs.forEach((r) => sectionRows.push(r)));
          const sectionAvg = sectionRows.length === 0
            ? 0
            : Math.round(sectionRows.reduce((acc, r) => acc + (r.percent_status ?? 0), 0) / sectionRows.length);
          return (
          <section key={section} className="glass-surface rounded-xl border border-white/[0.06] overflow-hidden">
            <header className="px-5 py-3 bg-cubus-lime/10 border-b border-cubus-lime/30 flex items-center justify-between gap-4">
              <h3 className="text-cubus-lime font-bold tracking-wide text-sm">{section}</h3>
              {/* Mini barra + % de la sección — solo aparece si hay filas
                  con datos para promediar. */}
              <div className="flex items-center gap-3 min-w-[180px] max-w-[280px] flex-1">
                <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div
                    className="h-full transition-all duration-500"
                    style={{
                      width: `${sectionAvg}%`,
                      background: 'linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #DDFF00 100%)',
                    }}
                  />
                </div>
                <span className="text-xs font-bold text-cubus-lime tabular-nums w-10 text-right">{sectionAvg}%</span>
              </div>
            </header>
            <div className="divide-y divide-white/[0.05]">
              {Array.from(subMap.entries()).map(([sub, subRows]) => (
                <div key={sub}>
                  <div className="px-5 py-2 bg-white/[0.02]">
                    <span className="text-xs uppercase tracking-[2px] text-white/50 font-semibold">{sub}</span>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-white/40">
                        <th className="text-left  px-5 py-2 w-[22%]">Actividad</th>
                        <th className="text-center px-1 py-2 w-20">Ubicación</th>
                        <th className="text-center px-1 py-2 w-24">Formato</th>
                        {activeSheet === 'laminas' && (
                          <th className="text-center px-1 py-2 w-32">Escalas</th>
                        )}
                        <th className="text-center px-1 py-2 w-12">LOD</th>
                        <th className="text-center px-1 py-2 w-10" title="Pertenece a Entrega 1">E1</th>
                        <th className="text-center px-1 py-2 w-10" title="Pertenece a Entrega 2">E2</th>
                        <th className="text-center px-1 py-2 w-10" title="Pertenece a Entrega 3">E3</th>
                        <th className="text-left  px-3 py-2">Anotación</th>
                        <th className="text-center px-1 py-2 w-16">Estado</th>
                        <th className="text-left  px-2 py-2 w-40">Barra de Estado</th>
                        <th className="text-center px-1 py-2 w-12" title="Marcar como completada (100%)">✓</th>
                        <th className="text-center px-2 py-2 w-20">Nivel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subRows.map((r) => {
                        const completed = isCompleted(r);
                        return (
                          <tr
                            key={r.id}
                            className={`border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors ${
                              completed ? 'bg-cubus-lime/[0.06]' : ''
                            }`}
                          >
                            <td className="px-5 py-2">
                              <span className={completed ? 'text-cubus-lime font-medium' : 'text-white/90'}>
                                {r.activity}
                              </span>
                            </td>
                            <td className="text-center text-white/60 text-[11px] uppercase tracking-wider">
                              {r.location ?? ''}
                            </td>
                            <td className="text-center text-white/50 text-[11px] uppercase">
                              {r.file_format ?? ''}
                            </td>
                            {activeSheet === 'laminas' && (
                              <td className="text-center text-white/50 text-[11px]">
                                {r.scales ?? ''}
                              </td>
                            )}
                            <td className="text-center text-white/60 text-xs">{r.lod ?? ''}</td>
                            {/* Indicadores E1/E2/E3 — read-only, muestran a qué
                                entregas pertenece la fila (no editables). */}
                            {(['e1', 'e2', 'e3'] as const).map((eN) => {
                              const applies = r[`delivery_${eN}` as const];
                              return (
                                <td key={eN} className="text-center">
                                  {applies ? (
                                    <Check className="w-3.5 h-3.5 text-cubus-lime/80 inline-block" />
                                  ) : (
                                    <span className="text-white/15">·</span>
                                  )}
                                </td>
                              );
                            })}
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={r.notes ?? ''}
                                disabled={!editable}
                                onChange={(e) => onNotesChange?.(r, e.target.value)}
                                placeholder={editable ? 'Agregar nota…' : ''}
                                className="w-full bg-transparent border border-transparent hover:border-white/[0.08] focus:border-cubus-lime/40 rounded px-2 py-1 text-white/80 text-xs focus:outline-none disabled:cursor-not-allowed"
                              />
                            </td>
                            {/* ESTADO: input numérico 0-100 */}
                            <td className="text-center px-1">
                              <input
                                type="number"
                                min={0}
                                max={100}
                                step={5}
                                value={r.percent_status}
                                disabled={!editable}
                                onChange={(e) => {
                                  const v = Math.max(0, Math.min(100, parseInt(e.target.value || '0', 10)));
                                  onPercentChange?.(r, v);
                                }}
                                className="w-14 bg-transparent border border-white/[0.08] hover:border-cubus-lime/40 focus:border-cubus-lime rounded px-1 py-1 text-center text-white/90 text-xs focus:outline-none disabled:cursor-not-allowed"
                                title="Avance manual (0-100)"
                              />
                            </td>
                            {/* BARRA DE ESTADO: gradiente rojo→amarillo→lime según % */}
                            <td className="px-2">
                              <ProgressBar percent={r.percent_status} />
                            </td>
                            {/* Checkbox "completar": al marcar pone 100%, al
                                desmarcar pone 0%. Mantiene la lógica de "filas
                                completas se pintan verdes". */}
                            <td className="text-center px-1">
                              <input
                                type="checkbox"
                                checked={completed}
                                disabled={!editable}
                                onChange={(e) => onPercentChange?.(r, e.target.checked ? 100 : 0)}
                                className="w-4 h-4 accent-cubus-lime cursor-pointer disabled:cursor-not-allowed"
                                title={completed ? 'Marcar como pendiente' : 'Marcar como completada (100%)'}
                              />
                            </td>
                            <td className="text-center px-2">
                              {r.is_leveled && editable && (
                                <div className="inline-flex items-center gap-1">
                                  {isLastOfLevelGroup(r) && (
                                    <button
                                      type="button"
                                      onClick={() => onAddLevel?.(r)}
                                      className="w-6 h-6 rounded bg-cubus-lime/10 hover:bg-cubus-lime/20 text-cubus-lime flex items-center justify-center transition-colors"
                                      title="Agregar nivel"
                                      aria-label="Agregar nivel"
                                    >
                                      <Plus className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => onRemoveRow?.(r)}
                                    className="w-6 h-6 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 flex items-center justify-center transition-colors"
                                    title="Quitar este nivel"
                                    aria-label="Quitar este nivel"
                                  >
                                    <Minus className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </section>
          );
        })}

        {grouped.size === 0 && (
          <div className="text-center text-muted py-12">No hay filas en esta pestaña.</div>
        )}
      </div>
    </div>
  );
}

/**
 * Barra de estado con degradé del rojo (0%) al lime CUBUS (100%).
 * Pasa por amarillo en el medio para dar feedback visual del avance.
 */
function ProgressBar({ percent }: { percent: number }) {
  const p = Math.max(0, Math.min(100, percent));
  // Color del relleno según %. Interpola en 3 tramos: rojo → amber → lime.
  // No hace falta una función JS — usamos un linear-gradient horizontal y
  // mostramos solo la parte hasta `percent` con un wrapper que recorta.
  return (
    <div className="w-full">
      <div className="relative h-2 rounded-full bg-white/[0.05] overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 transition-all duration-300"
          style={{
            width: `${p}%`,
            background: 'linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #DDFF00 100%)',
            backgroundSize: `${10000 / Math.max(p, 1)}% 100%`,
          }}
        />
      </div>
      <div className="mt-0.5 text-[10px] text-white/40 text-right tabular-nums">{p}%</div>
    </div>
  );
}

function SheetTab({
  icon: Icon, label, count, active, onClick,
}: {
  icon: any; label: string; count: number; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-all border-b-2 ${
        active
          ? 'border-cubus-lime text-cubus-lime'
          : 'border-transparent text-white/60 hover:text-white'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
      <span className="text-xs opacity-60">({count})</span>
    </button>
  );
}
